interface FormConfigurationSectionFields<T> {
    name: string;
    categories: Array<{ name: string; elements: T[] }>;
}

interface FormConfigurationDatasetFields<T> {
    initializationSection: FormConfigurationSectionFields<T>;
    sections: FormConfigurationSectionFields<T>[];
}

export interface FormConfigurationFieldLookupEntry<T> {
    formConfiguration: T;
    section?: string;
    category?: string;
}

export type FormConfigurationFieldLocationLookup<T> = Record<
    string,
    Record<string, FormConfigurationFieldLookupEntry<T>[]>
>;

/**
 * Builds a per-dataset field index while preserving the first match and its section/category.
 */
export function buildFormConfigurationFieldLookup<T extends { name: string }>(
    datasets: Record<string, FormConfigurationDatasetFields<T>>
): FormConfigurationFieldLocationLookup<T> {
    const lookup = Object.create(null) as FormConfigurationFieldLocationLookup<T>;

    for (const [datasetName, dataset] of Object.entries(datasets)) {
        const fieldsByName = Object.create(null) as Record<
            string,
            FormConfigurationFieldLookupEntry<T>[]
        >;

        for (const section of dataset.sections) {
            for (const category of section.categories) {
                for (const field of category.elements) {
                    (fieldsByName[field.name] ??= []).push({
                        formConfiguration: field,
                        section: section.name,
                        category: category.name
                    });
                }
            }
        }

        for (const category of dataset.initializationSection.categories) {
            for (const field of category.elements) {
                (fieldsByName[field.name] ??= []).push({
                    formConfiguration: field
                });
            }
        }

        lookup[datasetName] = fieldsByName;
    }

    return lookup;
}
